import { useState } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { sendPasswordResetEmail, type User } from 'firebase/auth'
import { KeyRound, Loader2, ChevronRight } from 'lucide-react'
import { auth } from '@/lib/firebase'
import { track } from '@/lib/analytics'
import { log } from '@/lib/logger'
import { passwordResetSentMessage } from '@/lib/commonStrings'
import { SettingsRow } from '@/components/SettingsCard'

const PROVIDER_NAMES: Record<string, string> = {
  'google.com': 'Google',
  'apple.com': 'Apple',
}

/**
 * Account-settings row for the user's password. Email/password accounts get a
 * "Change password" action that emails a reset link; social-only accounts get
 * a read-only row saying which provider they sign in with, so someone who came
 * looking for a password (see #390) learns there isn't one instead of finding
 * nothing.
 */
export function PasswordSettingsRow({ user }: { user: User }) {
  const [sending, setSending] = useState(false)
  const providerIds = user.providerData.map(p => p.providerId)
  const hasPassword = providerIds.includes('password')
  const socialProvider = providerIds.map(id => PROVIDER_NAMES[id]).find(Boolean)

  if (!hasPassword) {
    if (!socialProvider || !user.email) return null
    return (
      <SettingsRow
        icon={<KeyRound className="size-4" />}
        label={<fbt desc="Row in account settings for a user with no password">Password</fbt>}
        description={
          <fbt desc="Explains that a social-login account has no password to change">
            You sign in with <fbt:param name="provider">{socialProvider}</fbt:param>, so there's
            no Faro password to change.
          </fbt>
        }
      />
    )
  }

  async function handleReset() {
    if (sending || !user.email) return
    setSending(true)
    try {
      await sendPasswordResetEmail(auth, user.email)
      track('password_reset_requested', { entry_point: 'settings' })
      toast.success(passwordResetSentMessage())
    } catch (err) {
      log('auth', 'password reset email failed', { code: (err as { code?: string })?.code })
      toast.error(
        fbt(
          "Couldn't send the reset email. Please try again.",
          'Toast shown when sending a password reset email from Settings fails',
        ).toString(),
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <SettingsRow
      icon={sending ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
      label={<fbt desc="Row in account settings that emails a password reset link">Change password</fbt>}
      description={
        <fbt desc="Explains what tapping Change password does">
          We'll email a reset link to <fbt:param name="email">{user.email}</fbt:param>.
        </fbt>
      }
      trailing={<ChevronRight className="size-4 text-muted-foreground" />}
      onClick={handleReset}
      disabled={sending}
    />
  )
}
